import enUs from 'date-fns/locale/en-US';
import { nanoid } from 'nanoid';
import * as React from 'react';
import { FormGroup, FormControl, InputGroup } from 'react-bootstrap';
import * as ReactDatePickerNS from 'react-datepicker';
import type ReactDatePickerType from 'react-datepicker';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DatePicker = ((ReactDatePickerNS as any).default?.default ?? (ReactDatePickerNS as any).default ?? ReactDatePickerNS) as typeof ReactDatePickerType;

import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import { bemBlock } from 'af-utils/bem.util';

import HorizontalAlignment from 'ab-enums/horizontalAlignment.enum';

import { UNIQUE_ID_SIZE } from 'ab-common/constants/value';

import Label from 'af-components/LockedValue/Label';
import type { OwnProps as TooltipProps } from 'af-components/Tooltip';
import Tooltip from 'af-components/Tooltip';

type PopperPlacementPositions = (
	'auto'
	| 'auto-start'
	| 'auto-end'
	| 'top'
	| 'top-start'
	| 'top-end'
	| 'bottom'
	| 'bottom-start'
	| 'bottom-end'
	| 'right'
	| 'right-start'
	| 'right-end'
	| 'left'
	| 'left-start'
	| 'left-end'
);

interface CalendarSettings<T> {
	minDate?: T;
	maxDate?: T;
	filterDate?: (date: T) => boolean;
	excludeDates?: T[];
	highlightDates?: T[];
}

export interface Props {
	id?: string;
	label?: string;
	isRequired?: boolean;
	disabled?: boolean;
	placeholder?: string;
	placeholderText?: string;
	onValueChange?: (newDate: Date) => void;
	onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
	onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
	suggestionText?: string | React.JSX.Element;
	isConnected?: boolean;
	horizontalAlignment?: HorizontalAlignment;
	popperPlacement?: PopperPlacementPositions;
	isStandalone?: boolean;
	autoFocus?: boolean;
	/**
	 * The date format that will be shown and saved.
	 *
	 * Should be be an ISO 8601 or RFC 2822 Date time format, otherwise moment.js might not accept it.
	 */
	dateFormat?: TimeFormat;
	originalDateFormat?: TimeFormat;
	locale?: Locale;	// from date-fns
	/**
	 * If this value is specified it will override the date that is shown on Date Picker.
	 */
	selectedValue?: Date | string;
	defaultValue?: Date;
	/**
	 * DatePicker attributes.
	 * For more attributes see {@link https://hacker0x01.github.io/react-datepicker }.
	 * To comply with v2, we are remapping moment values into dates in State
	 */
	calendarSettings?: CalendarSettings<Date>;
	handleSelect?: (changedDate: Date, event) => void;
	datePickerClassName?: string;
	/**
	 * This element (if exists) will be rendered at the bottom of the component. (Used for error or warning info).
	 */
	block?: React.JSX.Element;
	/**
	 * Used for giving IDs on elements that need to be targeted through automated tests
	 */
	wrapperId?: string;
	fixed?: boolean;
	tooltipMessage?: TooltipProps['message'];
	isClearable?: boolean;
	withAppend?: boolean;
	wrapperClassName?: string;
	showTodayButton?: boolean;
	appendClassName?: string;
	calendarIconAdditionalClassName?: string;
	error?: string;
}

interface DropdownStyle {
	top: number | 'auto';
	bottom: number | 'auto';
	left: number;
	width: number;
}

const CALENDAR_FALLBACK_HEIGHT = 300;
const CALENDAR_ANCHOR_GAP = 10;

const DateInput = (props: Props) => {
	const [wrapperId] = React.useState(props.wrapperId ?? nanoid(UNIQUE_ID_SIZE));
	const [autoPlacement, setAutoPlacement] = React.useState<'top' | 'bottom'>('bottom');
	const [isCalendarOpen, setIsCalendarOpen] = React.useState(false);
	const datePickerRef = React.useRef<ReactDatePickerType>(null);
	const calendarHeightRef = React.useRef(0);
	const popperDivRef = React.useRef<HTMLDivElement>(null);
	const popperStyleRef = React.useRef<DropdownStyle>({} as DropdownStyle);
	const fixedPopperModifiers = React.useMemo(() => ([{ name: 'flip', enabled: false }, { name: 'preventOverflow', enabled: false }]), []);

	const {
		id,
		disabled,
		label,
		isRequired,
		dateFormat: format = TimeFormat.DATE_ONLY,
		originalDateFormat: originalFormat = TimeFormat.ISO_DATETIME,
		locale = enUs,
		horizontalAlignment = HorizontalAlignment.LEFT,
		defaultValue,
		placeholderText,
		suggestionText,
		isConnected,
		popperPlacement = 'bottom',
		selectedValue,
		block,
		autoFocus = false,
		datePickerClassName = 'form-control',
		isStandalone = false,
		calendarSettings,
		tooltipMessage,
		isClearable,
		fixed = false,
		onBlur,
		onFocus,
		onValueChange,
		handleSelect: onSelect,
		withAppend = true,
		wrapperClassName,
		showTodayButton = true,
		appendClassName,
		calendarIconAdditionalClassName,
		error,
	} = props;

	const applyPopperStyle = React.useCallback(() => {
		const el = popperDivRef.current;
		if (!el) {
			return;
		}
		const { top, bottom, left, width } = popperStyleRef.current;
		el.style.top = typeof top === 'number' ? `${top}px` : top;
		el.style.bottom = typeof bottom === 'number' ? `${bottom}px` : bottom;
		el.style.left = `${left}px`;
		el.style.width = `${width}px`;
	}, []);

	const placeCalendar = React.useCallback(() => {
		const wrapper = document.getElementById(wrapperId);
		const rect = wrapper?.getClientRects()?.[0];
		if (!rect) {
			return;
		}
		const viewportHeight = window.innerHeight;
		const calendarHeight = calendarHeightRef.current || CALENDAR_FALLBACK_HEIGHT;
		const fitsBelow = rect.bottom + CALENDAR_ANCHOR_GAP + calendarHeight <= viewportHeight;
		const fitsAbove = rect.top - CALENDAR_ANCHOR_GAP - calendarHeight >= 0;
		if (fitsBelow || (!fitsAbove && viewportHeight - rect.bottom >= rect.top)) {
			setAutoPlacement('bottom');
			popperStyleRef.current = { top: rect.bottom, bottom: 'auto', left: rect.x, width: rect.width };
		}
		else {
			setAutoPlacement('top');
			popperStyleRef.current = { top: 'auto', bottom: viewportHeight - rect.top, left: rect.x, width: rect.width };
		}
		applyPopperStyle();
	}, [wrapperId, applyPopperStyle]);

	const handleFocus = React.useCallback((event: React.FocusEvent<HTMLInputElement>) => {
		placeCalendar();
		onFocus?.(event);
	}, [onFocus, placeCalendar]);

	const formGroupClassName = bemBlock('date-input', {
		left: horizontalAlignment === HorizontalAlignment.LEFT,
		right: horizontalAlignment !== HorizontalAlignment.LEFT,
		connected: !!isConnected,
		standalone: !!isStandalone,
	});

	const _selectedDate = selectedValue ?? defaultValue;
	const selectedDate = _selectedDate instanceof Date
		? _selectedDate
		: _selectedDate ? TimeUtils.parseDate(_selectedDate, originalFormat) : null;

	const handleSelect = React.useCallback((changedDate: Date, event: React.ChangeEvent<HTMLInputElement>) => {
		event.target.value = TimeUtils.formatDate(changedDate, format);
		// If we have redux logic, only handleSelect will be called
		if (onSelect) {
			onSelect(changedDate, event);
			return;
		}
		onValueChange?.(changedDate);
	}, [format, onSelect, onValueChange]);

	const handleOpen = React.useCallback(() => {
		if (!fixed) {
			return;
		}

		const calendarEl = document.getElementById(wrapperId)?.querySelector('.react-datepicker');
		if (calendarEl?.getBoundingClientRect().height) {
			calendarHeightRef.current = calendarEl.getBoundingClientRect().height;
		}

		setIsCalendarOpen(true);
		placeCalendar();
	}, [fixed, wrapperId, placeCalendar]);

	const handleClose = React.useCallback(() => {
		if (isCalendarOpen) {
			setIsCalendarOpen(false);
		}
	}, [isCalendarOpen]);

	React.useEffect(() => {
		if (!fixed || !isCalendarOpen) {
			return undefined;
		}

		let frameId = 0;

		const handleScrollOrResize = () => {
			if (frameId) {
				return;
			}
			frameId = window.requestAnimationFrame(() => {
				frameId = 0;
				placeCalendar();
			});
		};

		window.addEventListener('scroll', handleScrollOrResize, true);
		window.addEventListener('resize', handleScrollOrResize);

		return () => {
			if (frameId) {
				window.cancelAnimationFrame(frameId);
			}
			window.removeEventListener('scroll', handleScrollOrResize, true);
			window.removeEventListener('resize', handleScrollOrResize);
		};
	}, [fixed, isCalendarOpen, placeCalendar]);

	const popperContainer = React.useCallback((popperProps: { children: React.ReactNode[]; }) => {
		if (!fixed) {
			return <>{popperProps.children}</>;
		}

		return (
			<div
				className="date-input__popper date-input__popper--fixed"
				ref={popperDivRef}
			>
				{popperProps.children}
			</div>
		);
	}, [fixed]);

	const calendarClassName = React.useMemo(() => {
		const baseClassName = 'input-group-append--clickable input-group-append--icon input-group-append--open-left';
		if (appendClassName) {
			return baseClassName + ' ' + appendClassName;
		}
		return baseClassName;
	}, [appendClassName]);

	const onAppendClick = React.useCallback(() => {
		datePickerRef.current?.setFocus();
	}, []);

	const renderBody = React.useCallback(() => {
		return (
			<div
				className={`${wrapperClassName} date-input__wrapper`}
				id={wrapperId}
			>
				<DatePicker
					{...calendarSettings}
					autoComplete="off"
					autoFocus={autoFocus}
					className={datePickerClassName}
					dateFormat={format && TimeUtils.datePickerFormat(format)} // remove this to set the date format to depend on *locale* - make sure that moment.js format requirements are met
					disabled={disabled}
					disabledKeyboardNavigation
					dropdownMode="select"
					isClearable={isClearable}
					locale={locale}
					maxDate={calendarSettings?.maxDate ?? undefined}
					minDate={calendarSettings?.minDate ?? undefined}
					onBlur={onBlur}
					onCalendarClose={handleClose}
					onCalendarOpen={handleOpen}
					onChange={handleSelect}
					onFocus={fixed ? handleFocus : onFocus}
					placeholderText={placeholderText}
					popperContainer={popperContainer}
					popperModifiers={fixed ? fixedPopperModifiers : undefined}
					popperPlacement={fixed ? autoPlacement : popperPlacement}
					ref={datePickerRef}
					selected={selectedDate}
					showMonthDropdown={true}
					showYearDropdown={true}
					todayButton={showTodayButton ? 'Today' : null}
				/>
				{withAppend && (
					<InputGroup.Append
						className={calendarClassName}
						onClick={onAppendClick}
					>
						<span className={`icon-calendar ${calendarIconAdditionalClassName ? calendarIconAdditionalClassName : ''}`} />
					</InputGroup.Append>
				)}
			</div>
		);
	}, [
		autoFocus,
		autoPlacement,
		calendarClassName,
		calendarIconAdditionalClassName,
		calendarSettings,
		datePickerClassName,
		disabled,
		fixed,
		fixedPopperModifiers,
		format,
		handleFocus,
		handleOpen,
		handleSelect,
		handleClose,
		isClearable,
		locale,
		onAppendClick,
		onBlur,
		onFocus,
		placeholderText,
		popperContainer,
		popperPlacement,
		selectedDate,
		showTodayButton,
		withAppend,
		wrapperClassName,
		wrapperId,
	]);

	const renderWithTooltip = React.useCallback((_error: string) => {
		return (
			<Tooltip containerClassName="date-input__tooltip-container" message={_error} placement="auto">
				{renderBody()}
			</Tooltip>
		);
	}, [renderBody]);

	return (
		<FormGroup className={formGroupClassName} controlId={id}>
			<div className="input-header">
				{label &&
					<Label
						isRequired={isRequired}
						label={label}
						tooltipMessage={tooltipMessage}
						tooltipPlacement="top"
						withMargin={true}
					/>
				}
			</div>
			{
				error
					? renderWithTooltip(error)
					: renderBody()
			}
			{suggestionText}
			<FormControl.Feedback />
			{block}
		</FormGroup>
	);
};

export default React.memo(DateInput);
